import { useMemo } from 'react';
//@ts-ignore
import Sentry from 'utils/sentry';
import { useUser } from 'utils/hooks/useUser';
import ExpediencyTest from 'components/ExpediencyTest';
import { FormValues, Payload } from 'components/ExpediencyTest/types';
import {
  useCheckExpediencyTest,
  useDelExpediencyTest,
  useGetExpediencyTest,
  usePostExpediencyTest,
} from 'utils/hooks/useExpediencyTest';
import { postSigning } from 'utils/hooks/useExpediencyTest/helpers';
import {
  checkIsPDFRender,
  checkIsCorpoAccount,
  getFormValues,
  getPayload,
  checkAccountId,
} from 'utils/expediencyTest';
import { FORM_ERROR } from 'final-form';
import { Redirect } from 'react-router-dom';
import { useSelectedAccount } from 'utils/hooks/useAccounts';
import { useLocation } from 'react-router-dom';
import { Account } from 'types/auth';
import { Text } from '@oms/ui-text';
import { Notification } from '@oms/ui-notification';

/** Previously called HTestPage */
export const ExpediencyTestPage = () => {
  const location = useLocation();

  const isPDFRender = checkIsPDFRender(location);
  const corpoAccount = checkIsCorpoAccount(location);
  const accountId = checkAccountId(location);
  const {
    data: { selectedAccount },
  } = useSelectedAccount();
  const userData = useUser();

  const {
    data: { userId = '', accounts },
  } = userData;
  const selAccount = selectedAccount as Account;
  let {
    data: expediencyTestFlag,
    isLoading,
    isCorpoAccount,
  } = useCheckExpediencyTest(userId as string, selAccount, accounts);

  if (corpoAccount) {
    isCorpoAccount = true;
  }

  if (userData?.data?.customerData?.mustTakeExpediencyTest) {
    expediencyTestFlag = true;
  }

  const { data: expediencyTest } = useGetExpediencyTest(
    isPDFRender ? accountId : selAccount?.id,
    isCorpoAccount,
    isPDFRender,
    location,
  );

  const { mutateAsync: deleteExpediencyTest } = useDelExpediencyTest();
  const { mutateAsync: postExpediencyTest } = usePostExpediencyTest(
    selAccount,
    isCorpoAccount,
  );

  const submit = (values: FormValues) => {
    const expediencyTestPayload: Payload = getPayload(values, isCorpoAccount);
    return submitResults(expediencyTestPayload, selAccount, isCorpoAccount);
  };

  let initialData = useMemo(() => {
    return getFormValues(expediencyTest as Payload);
  }, [expediencyTest]);

  const submitResults = async (
    expediencyTestPayload: Payload,
    account: Account,
    isCorpoAccount: boolean,
  ) => {
    Sentry.withScope(scope => {
      scope.setLevel('info');
      scope.setTag('type', 'expediency-test');
      scope.setExtras({
        expediencyTestPayload,
      });
      Sentry.captureMessage(
        `About to submit expediency test for user ${userId}`,
      );
    });

    try {
      if (expediencyTest) {
        await deleteExpediencyTest({
          userId,
          accountId: account?.id,
          corpoAccount: isCorpoAccount,
        });
      }
      const postPayload = {
        ...expediencyTestPayload,
        corpoAccount: isCorpoAccount,
      };
      await postExpediencyTest({
        payload: postPayload,
        userId,
        account,
        corpoAccount: isCorpoAccount,
      });
      await postSigning({
        userId,
        accountId: account?.id,
        isCorpoAccount,
        expediencyTest: JSON.stringify(postPayload),
      }).then((result: any) => {
        window.location.assign(result?.redirectUri);
      });
    } catch (e) {
      return { [FORM_ERROR]: e };
    }
  };

  const fail = isPDFRender && !expediencyTest;

  if (!expediencyTestFlag && !isLoading && !isPDFRender) {
    return <Redirect to="/" />;
  }

  return (
    <>
      {fail && (
        <Notification title="Feil ved innsending" status="error">
          <Text>
            Økten din er utløpt. Vennligst logg inn på nytt og fyll ut skjemaet.
          </Text>
        </Notification>
      )}
      {!fail && (
        <main>
          <ExpediencyTest
            initialData={initialData}
            onSubmit={submit}
            isPDFRender={isPDFRender}
            isCorpoAccount={isCorpoAccount}
            selectedAccount={selectedAccount as Account}
          />
        </main>
      )}
    </>
  );
};

export default ExpediencyTestPage;
